import { useMemo, useState } from 'react';

import { alpha, useTheme } from '@mui/material/styles';
import { ClickAwayListener, Input, InputAdornment, Slide, Stack } from '@mui/material';
import { MIcon, MIconButton } from '../../../@material-extend';
import SearchBarResultsList from './SearchBarResultsList';

import useAuth from '../../../../hooks/useAuth';

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const SearchBar = () => {
  const { get, isResource, isManager, isPartner } = useAuth();
  const theme = useTheme();
  const [isOpen, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(null);

  let searchPlaceholder = `Recherche (${isManager ? 'agent et ' : ''}site)`;
  if (isPartner) {
    searchPlaceholder = `Recherche (agent)`;
  }
  const searchIconVariant = window.env.isMobileDevice ? 'primary-lightest' : 'neutral-faded';

  const styleSearchBar = useMemo(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 99,

    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',

    height: APPBAR_MOBILE,
    width: '100%',
    padding: theme.spacing(0, 3),

    backgroundColor: window.env.isMobileDevice
      ? theme.palette.primary.main
      : `${alpha(theme.palette.background.default, 0.98)}`,
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
    [theme.breakpoints.up('md')]: {
      height: APPBAR_DESKTOP,
      padding: theme.spacing(0, 5)
    },
    '.MuiInput-root': {
      backgroundColor: window.env.isMobileDevice ? theme.palette.primary.light : theme.palette.background.neutral,
      color: window.env.isMobileDevice ? 'white' : null,
      borderRadius: theme.shape.borderRadiusXs,
      paddingLeft: 1,
      '& .MuiInput-input::placeholder': {
        color: window.env.isMobileDevice ? theme.palette.primary.lightest : 'text.primary'
      },
      '&::-ms-input-placeholder': {
        // Edge 12 -18
        color: window.env.isMobileDevice ? theme.palette.primary.lightest : 'text.primary'
      },
      '&::placeholder': {
        color: window.env.isMobileDevice ? theme.palette.primary.lightest : 'text.primary',
        opacity: 1
      }
    }
  }));

  const handleSearch = (event) => {
    setLoading(true);
    setSearchValue(event.target.value);
    clearTimeout(elapsed);
    setElapsed(setTimeout(() => fetchData(get, event.target.value, setSearchResults, setLoading), 750));
  };

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchValue('');
    setSearchResults([]);
    setLoading(false);
    clearTimeout(elapsed);
  };

  if (isResource) {
    return;
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        {!isOpen && (
          <MIconButton onClick={handleOpen}>
            <MIcon icon="search" variant={searchIconVariant} />
          </MIconButton>
        )}

        {isOpen && (
          <>
            <Slide direction="down" in={isOpen} mountOnEnter unmountOnExit>
              <Stack sx={styleSearchBar}>
                <Input
                  autoFocus
                  autoComplete="off"
                  fullWidth
                  disableUnderline
                  placeholder={searchPlaceholder}
                  startAdornment={
                    <InputAdornment position="start">
                      <MIcon icon="search" variant={searchIconVariant} />
                    </InputAdornment>
                  }
                  sx={{ height: 44 }}
                  value={searchValue}
                  onChange={handleSearch}
                />
              </Stack>
            </Slide>

            <SearchBarResultsList
              APPBAR_MOBILE={APPBAR_MOBILE}
              APPBAR_DESKTOP={APPBAR_DESKTOP}
              isOpen={isOpen}
              loading={loading}
              searchResults={searchResults}
              searchValue={searchValue}
              handleClose={handleClose}
            />
          </>
        )}
      </div>
    </ClickAwayListener>
  );
};

async function fetchData(get, searchValue, setSearchResults, setLoading) {
  if (!searchValue.length) {
    setSearchResults([]);
    setLoading(false);
    return;
  }

  const result = await get('/search', { searchValue });
  if (result.success) {
    setSearchResults(result.results);
  } else {
    setSearchResults([]);
  }
  setLoading(false);
}

export default SearchBar;
